/* eslint-disable */
import { Button } from "@mui/material";
import { CloseIcon } from "src/icons";
import React, { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import * as yup from "yup";
import moment from "moment";
import API from "src/network";
import { useRoleUser } from "src/hooks/query/usersByRole";
import { SlotsProps } from "src/pages/InterviewSlots/types";
import { confirmWrapper } from "src/components/Dialog";
import RHFDatePicker from "src/components/elements/CustomDatePicker/RHFDatePicker";
import RHFAutoComplete from "src/components/elements/AutoComplete/RHFAutoComplete";
import { Loader } from "src/components";

export interface MLiveFormValues {
  interviewDate: string;
  interviewerSlotId: number;
}

interface MLiveInterviewProps {
  onSubmit: (data: MLiveFormValues) => void;
  onCancel: () => void;
  defaultValues?: MLiveFormValues | undefined;
  loading: boolean;
}

const validationSchema = yup.object().shape({
  interviewDate: yup.string().required("Please select interview date"),
  interviewerSlotId: yup.number().required("Please select slot"),
});

const MLiveInterview = function MLiveInterview(props: MLiveInterviewProps) {
  const { onSubmit, defaultValues, onCancel, loading } = props;
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const resolver = useYupValidationResolver(validationSchema);

  const methods = useForm<MLiveFormValues>({
    defaultValues,
    resolver,
  });

  const { watch, handleSubmit } = methods;

  const { userByRoleOptions, usersLoading, fetchByRole } = useRoleUser({
    roleNameList: "ATSInterviewer",
  });

  const [slotOptions, setSlotOptions] = useState<
    { value: number; label: string }[]
  >([]);

  //   Create a lookup object from userByRoleOptions
  const interviewerLookup: Record<number, string> = userByRoleOptions.reduce(
    (acc, interviewer) => {
      acc[interviewer.value] = interviewer.label;
      return acc;
    },
    {} as Record<number, string>
  );

  const formatOptions = (slots: Array<SlotsProps>) => {
    return slots.map((item: SlotsProps) => ({
      value: item.id,
      label: `${item.startTime} To ${item.endTime} ${item.timeZone} :: ${
        interviewerLookup[item.interviewerId]
      }`,
    }));
  };

  const getInterviewSlots = async (date: string) => {
    const requestData = {
      date,
      statuses: ["OPEN"],
    };
    try {
      const fetchedSlots = await API.getInstance().getDaySlots(requestData);
      const formattedSlots = formatOptions(fetchedSlots);
      setSlotOptions(formattedSlots);
    } catch (e) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    if (userByRoleOptions.length === 0) fetchByRole();
  }, []);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    const date = watch("interviewDate");
    if (date) {
      const formattedDate = moment(date).format("MM/DD/YYYY");
      getInterviewSlots(formattedDate);
    }
  }, [watch("interviewDate")]);

  const submitHandler: SubmitHandler<MLiveFormValues> = async (data) => {
    setDisableSubmit(true);
    await onSubmit(data);
    setDisableSubmit(false);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(submitHandler)}
        className="bg-white p-10 flex gap-8 justify-center items-center flex-col"
      >
        <div className="flex w-full flex-col gap-10 mb-5">
          <div className="text-lg text-secondary font-bold text-center ">
            Schedule MLive Interview
          </div>
          <div className="flex gap-5">
            <div className="w-[150px]">
              <RHFDatePicker 
              control={methods.control} 
              name="interviewDate"
              minDate={moment(new Date())} 
              />
            </div>
            <div className="w-[300px]">
              <RHFAutoComplete
                control={methods.control}
                name="interviewerSlotId"
                placeholder="Interview Slots"
                options={slotOptions}
              />
            </div>
          </div>
        </div>
        <div className="w-full flex  gap-2 ">
          <Button
            variant="contained"
            type="submit"
            size="medium"
            className="font-medium rounded-full"
            disabled={disableSubmit}
          >
            Send
          </Button>
          <Button
            variant="outlined"
            size="medium"
            className="font-medium rounded-full"
            onClick={onCancel}
            disabled={disableSubmit}
          >
            Cancel
          </Button>
        </div>
        <div className="absolute right-2 top-2" onClick={onCancel}>
          <CloseIcon className="cursor-pointer" />
        </div>
        {loading && (
          <div className="absolute top-[40%] left-[40%]">
            <Loader />
          </div>
        )}
      </form>
    </FormProvider>
  );
};

MLiveInterview.defaultProps = {};

MLiveInterview.propTypes = {};

export default MLiveInterview;
